import { useEffect, useState } from 'react';
import {
  Alert, CircularProgress, DialogContent, DialogTitle,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { listSubscriptions, setSubscription } from './api';
import { apiErrorMessage } from '@/lib/errors';
import type { Subscription } from './api';
import { GlassDialog } from '@/components/GlassDialog';

const fmtDate = (s: string | null) => (s ? format(parseISO(s), 'dd/MM/yyyy') : '—');

export function SubscriptionsDialog({
  companyId, open, onClose, basePath = '/api/companies',
}: { companyId: number | null; open: boolean; onClose: () => void; basePath?: string }) {
  const [rows, setRows] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    const id = companyId;
    if (!open || id == null) return;
    let active = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await listSubscriptions(id as number, basePath);
        if (active) setRows(data);
      } catch {
        if (active) setError('ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໄດ້');
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => { active = false; };
  }, [open, companyId, basePath]);

  async function toggle(sub: Subscription) {
    if (companyId == null || busyId != null) return;
    const next = !sub.isSubscripted;
    // Turning a subscription OFF cuts the tenant's access — confirm first.
    if (!next && !window.confirm('ປິດການສະໝັກໃຊ້ຂອງບໍລິສັດນີ້?')) return;
    setBusyId(sub.id);
    // Optimistic update
    setRows((prev) => prev.map((r) => r.id === sub.id ? { ...r, isSubscripted: next } : r));
    try {
      await toast.promise(
        setSubscription(companyId, sub.id, next, basePath),
        { loading: 'ກຳລັງອັບເດດ...', success: 'ອັບເດດສຳເລັດ', error: (e) => apiErrorMessage(e, 'ອັບເດດບໍ່ສຳເລັດ') },
      );
    } catch {
      // Revert on error
      setRows((prev) => prev.map((r) => r.id === sub.id ? { ...r, isSubscripted: sub.isSubscripted } : r));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <GlassDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>ການສະໝັກໃຊ້</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <CircularProgress size={28} sx={{ display: 'block', mx: 'auto', my: 3 }} />
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>ບໍ່ມີຂໍ້ມູນ</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ໄອດີ</TableCell>
                  <TableCell>ເລີ່ມ</TableCell>
                  <TableCell>ສິ້ນສຸດ</TableCell>
                  <TableCell>ໃຊ້ງານ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((sub) => (
                  <TableRow key={sub.id} hover>
                    <TableCell>{sub.id}</TableCell>
                    <TableCell>{fmtDate(sub.startDate)}</TableCell>
                    <TableCell>{fmtDate(sub.endDate)}</TableCell>
                    <TableCell>
                      <Switch
                        checked={sub.isSubscripted}
                        onChange={() => { void toggle(sub); }}
                        disabled={busyId != null}
                        size="small"
                        slotProps={{ input: { 'aria-label': `subscription-${sub.id}` } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </GlassDialog>
  );
}
